/**
 * Float-precision helpers for AI-suggested values.
 *
 * An AI value arrives as a JSON double (shortest-repr spelling, e.g.
 * '65.8562682042') while the numeric(31,15) DB column stores the SAME double's
 * binary expansion rounded to 15dp ('65.856268204200006'). Raw string
 * comparison treats the two spellings of one double as different values, so
 * echo suggestions drew misleading "same -> same" merge arrows. Numeric values
 * must therefore compare in double space; only non-numeric input falls back to
 * the exact string compare.
 *
 * Lives in utilities so conditions.tsx (in-grid viewer choice) and
 * reviewAggregator.ts (card Merge/New derivation) share ONE comparison rule.
 */

/** The value as a finite number, or undefined when it does not parse as one.
 *
 * String parsing is LENIENT about money spelling: AI-extracted tokens keep
 * their document spelling ("$133.5", "1,234.50"), and treating those as
 * non-numeric made them render verbatim (the SCAL-9263 mixed-decimals bug)
 * and compare as strings. Currency symbols, thousands separators and
 * whitespace are stripped; '%' is NOT ('5%' is a different value than '5').
 */
export const finiteNumberOf = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string') {
    const stripped = value.replace(/[$€£¥,\s]/g, '');
    if (stripped === '') {
      return undefined;
    }
    const num = Number(stripped);
    return Number.isFinite(num) ? num : undefined;
  }
  return undefined;
};

/**
 * Whether two raw values are the SAME value: compared in double space when both
 * sides parse as finite numbers, by exact string otherwise.
 */
export const sameAiValue = (a: unknown, b: unknown): boolean => {
  const numA = finiteNumberOf(a);
  const numB = finiteNumberOf(b);
  if (numA !== undefined && numB !== undefined) {
    return numA === numB;
  }
  if (a !== null && b !== null && (typeof a === 'object' || typeof b === 'object')) {
    if (Array.isArray(a) || Array.isArray(b)) {
      return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((value, index) => sameAiValue(value, b[index]));
    }
    if (typeof a !== 'object' || typeof b !== 'object') {
      return false;
    }
    const left = a as Record<string, unknown>;
    const right = b as Record<string, unknown>;
    const keys = Object.keys(left);
    return keys.length === Object.keys(right).length && keys.every(key => key in right && sameAiValue(left[key], right[key]));
  }
  return String(a) === String(b);
};
